import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

interface ApplicationData {
  firstName: string;
  lastName: string;
  email: string;
  socialLink: string | null;
}

export default function CreatorCompleteSignup() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [formData, setFormData] = useState({
    referralCode: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) {
      setError("No setup token provided. Please use the link from your approval email.");
      setLoading(false);
      return;
    }

    fetch(`/api/creator/validate-token?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Invalid token");
        }
        return res.json();
      })
      .then((data) => {
        setApplicationData(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.acceptTerms) {
      toast({
        title: "Terms required",
        description: "Please accept the Terms & Conditions to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiRequest('POST', '/api/creator/complete-signup', {
        token,
        referralCode: formData.referralCode,
        password: formData.password,
      });
      setCompleted(true);
      toast({
        title: "Account activated!",
        description: "Welcome to the EduFocal Creator Program.",
      });
      setTimeout(() => setLocation('/ref/dashboard'), 2000);
    } catch (error: any) {
      toast({
        title: "Setup failed",
        description: error.message || "Unable to complete setup. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-chart-2" />
            <p className="text-muted-foreground">Validating your setup link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Setup Link Invalid</h3>
            <p className="text-muted-foreground text-center max-w-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="w-16 h-16 rounded-full bg-chart-2/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-chart-2" />
            </div>
            <h3 className="text-xl font-semibold text-foreground">Account Activated!</h3>
            <p className="text-muted-foreground text-center max-w-sm">
              Your Creator account is ready. Redirecting to your dashboard...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-chart-2 via-chart-2/90 to-primary flex items-center justify-center p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-lg mx-auto space-y-6">
        <div className="text-center space-y-3">
          <img src={logoUrl} alt="EduFocal" className="h-10 sm:h-12 w-auto mx-auto" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Complete Your Creator Account
          </h1>
          <p className="text-white/80 text-sm sm:text-base">
            Just a few more steps to get started
          </p>
        </div>

        <Card className="w-full">
          <CardHeader className="pb-4 p-4 sm:p-6">
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Your Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="text-sm font-medium text-foreground">{applicationData?.firstName} {applicationData?.lastName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm font-medium text-foreground">{applicationData?.email}</p>
                </div>
                {applicationData?.socialLink && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-muted-foreground">Social Link</p>
                    <p className="text-sm font-medium text-foreground break-all">{applicationData.socialLink}</p>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="referralCode">Choose Your Referral Code</Label>
                <Input
                  id="referralCode"
                  placeholder="e.g., mycode123"
                  value={formData.referralCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, referralCode: e.target.value }))}
                  required
                  minLength={3}
                  maxLength={20}
                  pattern="[a-zA-Z0-9_-]+"
                  data-testid="input-creator-setup-code"
                />
                <p className="text-xs text-muted-foreground">
                  Alphanumeric characters, 3-20 chars. This will be in your referral links.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Create Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min 6 characters"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    minLength={6}
                    data-testid="input-creator-setup-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    minLength={6}
                    data-testid="input-creator-setup-confirm-password"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
                  className="mt-1"
                  data-testid="checkbox-creator-terms"
                />
                <Label htmlFor="acceptTerms" className="text-sm text-muted-foreground cursor-pointer">
                  I accept the <a href="/terms" target="_blank" className="text-chart-2 underline underline-offset-2">Terms & Conditions</a> of the EduFocal Creator Program
                </Label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-4"
                size="lg"
                data-testid="button-creator-activate"
              >
                {isSubmitting ? "Activating..." : "Activate Creator Account"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
